"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Menu, X, Search, ChevronDown, MessageCircle, Phone } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchExpanded, setSearchExpanded] = useState(false);
  const toggleMobileSubmenu = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              {item.children && item.children.length > 0 ? <>
                  <NavigationMenuTrigger className="relative group px-4 py-2 text-sm font-medium text-foreground transition-colors duration-200 hover:!bg-transparent focus:!bg-transparent hover:text-primary focus:text-primary data-[state=open]:text-primary">
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 ease-out-back group-hover:w-full group-data-[state=open]:w-full" />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="!z-[100]">
                    <ul className="sub-menu grid gap-1 p-2 w-[200px]">
                      {item.children.map((subItem, j) => <li key={subItem.id} data-index={j}>
                          <NavigationMenuLink asChild>
                            <Link to={subItem.href || "#"} className="block px-4 py-2 text-sm text-foreground hover:bg-primary-light rounded-md transition-colors duration-200">
                              {subItem.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="relative group px-4 py-2 text-sm font-medium text-foreground transition-colors duration-200 hover:!bg-transparent focus:!bg-transparent hover:text-primary focus:text-primary">
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 ease-out-back group-hover:w-full" />
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-t shadow-medium md:hidden z-50">
          <nav className="container mx-auto px-4 py-4">
            <ul className="flex flex-col gap-2">
              {menuItems.map((item, i) => <li key={item.id} data-index={i}>
                  {item.children && item.children.length > 0 ? <div>
                      <Button variant="ghost" className="w-full justify-between px-4 py-3 text-sm font-medium text-foreground hover:bg-primary-light rounded-md transition-colors duration-200" onClick={() => toggleMobileSubmenu(item.id)}>
                        {item.label}
                        <ChevronDown className={`size-4 transition-transform duration-200 ${expandedItems.has(item.id) ? "rotate-180" : ""}`} />
                      </Button>
                      {expandedItems.has(item.id) && <ul className="sub-menu mt-1 ml-4 flex flex-col gap-1">
                          {item.children.map((subItem, j) => <li key={subItem.id} data-index={j}>
                              <Link to={subItem.href || "#"} className="block px-4 py-2 text-sm text-foreground hover:bg-primary-light rounded-md transition-colors duration-200" onClick={() => setMobileMenuOpen(false)}>
                                {subItem.label}
                              </Link>
                            </li>)}
                        </ul>}
                    </div> : <Link to={item.href || "#"} className="block px-4 py-3 text-sm font-medium text-foreground hover:bg-primary-light rounded-md transition-colors duration-200" onClick={() => setMobileMenuOpen(false)}>
                      {item.label}
                    </Link>}
                </li>)}
            </ul>
          </nav>
        </div>}

      <div className="hidden md:flex items-center gap-3">
        <div className={`flex items-center gap-2 transition-all duration-300 ${searchExpanded ? "w-48" : "w-9"}`} onMouseEnter={() => setSearchExpanded(true)} onMouseLeave={() => setSearchExpanded(false)}>
          <Search className="size-5 text-muted-foreground shrink-0" />
          {searchExpanded && <Input type="search" placeholder="Search..." className="h-9 text-sm animate-fade-in-up" />}
        </div>

        <Button asChild variant="secondary" className="bg-secondary text-white hover:bg-secondary-hover active:bg-secondary-active transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium focus:bg-secondary">
          <Link to="/#contact">Book Free Consultation</Link>
        </Button>

        <Link to="tel:+447355792647" className="flex items-center gap-2 px-3 py-1.5 bg-primary-light rounded-md border border-primary/10">
          <Phone className="size-4 text-primary" />
          <span className="text-sm font-semibold text-primary">+44 7355 792647</span>
        </Link>
      </div>
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
       <header className="sticky top-0 z-50 w-full bg-transparent  backdrop-blur-md transition-all duration-300 !h-[100px]">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <Link to="/" className="shrink-0 hover:opacity-80 transition-opacity">
            <img src="https://educationfamilytutors.com/wp-content/uploads/2026/01/Copy-of-New-Education-Family-Logo.png" alt="Education Family Tutors" className="!w-[140px] !h-[140px]" />
          </Link>

          <MenuProvider menu_id="3" className="flex items-center gap-6">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>
      </header>

      <Link to="https://wa.me/447355792647?text=Hi%20Education%20Family%20Tutors%20%E2%80%94%20I%E2%80%99d%20like%20to%20enquire%20about%20tutoring" newTab className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-6 py-4 rounded-full shadow-strong hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 focus-ring" style={{
      backgroundColor: '#25D366'
    }} aria-label="Message us on WhatsApp">
        <MessageCircle className="w-6 h-6 text-white flex-shrink-0" />
        <span className="text-white font-semibold whitespace-nowrap hidden sm:inline">
          Message us on WhatsApp
        </span>
      </Link>
    </>;
}